// ============================================================================
//  TeamDashboard.jsx — the roster, grouped by team.
//
//  A member sees one section (his own team, now including his brothers). A
//  Team Admin sees the team he leads. An Admin sees every team he leads. A
//  Super Admin sees all of them plus an Unassigned bucket for men who belong
//  to no cohort yet — otherwise those men are invisible to everyone and quietly
//  pile up.
//
//  WHICH men appear at all is decided by RLS, never here. This file only
//  arranges what came back and decides which buttons to offer.
// ============================================================================

import React, { useState } from "react";
import { ChevronRight, Target, MoreVertical, UserMinus, UserCheck, Shield } from "lucide-react";
import { C, display, kicker } from "../theme/tokens.js";
import { LOGO_EQUILIBRIUM } from "../theme/logos.js";
import { Ring, RagPill, Avatar, Pill } from "../theme/primitives.jsx";
import { personGoals, overall, worstRag } from "../lib/metrics.js";
import { canManage, label as roleLabel, capacityOf, leadsTeam } from "../lib/roles.js";

const UNASSIGNED = "__unassigned__";

export default function TeamDashboard({ st, me, selfId, onOpen, onToggleSuspend }) {
  const people = Object.values(st.people);
  const led = st.myLedGroupIds ?? [];

  // Build the section list: teams the viewer can see, own team first.
  const byTeam = new Map();
  for (const p of people) {
    const key = p.group_id ?? UNASSIGNED;
    if (!byTeam.has(key)) byTeam.set(key, []);
    byTeam.get(key).push(p);
  }

  const myTeam = st.people[selfId]?.group_id ?? null;
  const sections = [...byTeam.keys()].sort((a, b) => {
    if (a === myTeam) return -1;
    if (b === myTeam) return 1;
    if (a === UNASSIGNED) return 1;
    if (b === UNASSIGNED) return -1;
    return (st.groups?.[a]?.name ?? "").localeCompare(st.groups?.[b]?.name ?? "");
  });

  const multiTeam = sections.filter((s) => s !== UNASSIGNED).length > 1;

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px 80px" }}>
      <div style={{ padding: "36px 0 26px" }}>
        <img src={LOGO_EQUILIBRIUM} alt="Equilibrium"
          style={{ height: 64, width: "auto", display: "block", marginBottom: 22 }} />
        <div style={kicker}>The Brotherhood</div>
        <h1 style={{ ...display, fontSize: 36, margin: "8px 0 6px", color: C.text }}>
          {multiTeam ? "The Men You Lead" : "The Men You Walk With"}
        </h1>
        <p style={{ fontSize: 14, color: C.sub, maxWidth: 580, lineHeight: 1.6 }}>
          Every man's game plan at a glance for the monthly Mile Marker meeting.
          Tap a man to drill into his goals.
        </p>
      </div>

      {sections.map((key) => (
        <TeamSection
          key={key}
          groupId={key === UNASSIGNED ? null : key}
          team={st.groups?.[key]}
          men={byTeam.get(key)}
          st={st} me={me} selfId={selfId} led={led}
          showHeading={sections.length > 1 || multiTeam}
          onOpen={onOpen} onToggleSuspend={onToggleSuspend}
        />
      ))}
    </div>
  );
}

function TeamSection({ groupId, team, men, st, me, selfId, led, showHeading, onOpen, onToggleSuspend }) {
  const canSeeSuspended = groupId
    ? leadsTeam(me, led, groupId)
    : me?.role === "super_admin";

  const active = [
    ...men.filter((p) => p.id === selfId && !p.is_suspended),
    ...men.filter((p) => p.id !== selfId && !p.is_suspended),
  ];
  const suspended = men.filter((p) => p.is_suspended);
  const cap = groupId ? capacityOf(st.people, st.groups, groupId) : null;
  const leaderIds = groupId ? (st.leaders?.[groupId] ?? []) : [];

  return (
    <div style={{ marginBottom: 34 }}>
      {showHeading && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
          marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${C.line}` }}>
          <h2 style={{ ...display, fontSize: 21, margin: 0, color: C.text }}>
            {groupId ? (team?.name ?? "Team") : "Unassigned"}
          </h2>
          {cap ? (
            <span style={{ fontSize: 12, color: cap.full ? C.redDk : C.sub, fontWeight: cap.full ? 700 : 400 }}>
              {cap.used} of {cap.capacity} men{cap.full ? " · full" : ""}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: C.sub }}>
              {men.length} man{men.length !== 1 ? "men" : ""} with no team yet
            </span>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 16 }}>
        {active.map((p) => (
          <TeamCard key={p.id} p={p} st={st} me={me} selfId={selfId} led={led}
            isLeader={leaderIds.includes(p.id)}
            onOpen={onOpen} onToggleSuspend={onToggleSuspend} />
        ))}
      </div>

      {canSeeSuspended && suspended.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ ...kicker, color: C.sub, marginBottom: 12 }}>
            Suspended ({suspended.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 16 }}>
            {suspended.map((p) => (
              <TeamCard key={p.id} p={p} st={st} me={me} selfId={selfId} led={led}
                isLeader={leaderIds.includes(p.id)}
                onOpen={onOpen} onToggleSuspend={onToggleSuspend} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TeamCard({ p, st, me, selfId, led, isLeader, onOpen, onToggleSuspend }) {
  const o = overall(st, p.id);
  const gc = personGoals(st, p.id).length;
  const wr = worstRag(st, p.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const manageable = canManage(me, p, led);
  const isSuspended = !!p.is_suspended;

  async function handleToggle(e) {
    e.stopPropagation();
    setMenuOpen(false);
    const verb = isSuspended ? "unsuspend" : "suspend";
    if (!window.confirm(`Are you sure you want to ${verb} ${p.name}?`)) return;
    setBusy(true);
    try {
      await onToggleSuspend(p.id, !isSuspended);
    } catch (err) {
      alert(err?.message || `Failed to ${verb}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <button className="mm-card" onClick={() => onOpen(p.id)} style={{
        textAlign: "left", cursor: "pointer", padding: "20px", borderRadius: 16, width: "100%",
        border: `1px solid ${C.line}`, background: C.panel, boxShadow: "0 1px 2px rgba(19,34,41,0.04)",
        opacity: isSuspended ? 0.55 : 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Avatar person={p} size={52} />
          <div style={{ position: "relative" }}>
            <Ring value={o} size={52} stroke={5} />
            <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
              ...display, fontSize: 13, color: C.text }}>{o}%</span>
          </div>
        </div>
        <div style={{ ...display, fontSize: 20, marginTop: 14, color: C.text }}>{p.name}</div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {isSuspended
            ? <Pill label="Suspended" />
            : wr ? <RagPill rag={wr} />
                 : <span style={{ fontSize: 11.5, color: C.faint }}>No marks yet</span>}
          {isLeader && <Pill label="Leads" tone="red" />}
          {p.role !== "mentee" && <Pill label={roleLabel(p.role)} tone="ink" />}
        </div>

        <div style={{ fontSize: 12.5, color: C.sub, marginTop: 12, display: "flex", alignItems: "center", gap: 6,
          borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
          <Target size={13} /> {gc} goal{gc !== 1 ? "s" : ""} tracked
          <ChevronRight size={14} style={{ marginLeft: "auto" }} />
        </div>
      </button>

      {manageable && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            disabled={busy}
            aria-label={`Manage ${p.name}`}
            style={{
              position: "absolute", top: 12, right: 12, width: 28, height: 28,
              border: "none", borderRadius: 6, background: "rgba(255,255,255,0.85)",
              cursor: busy ? "default" : "pointer", display: "grid", placeItems: "center",
              boxShadow: "0 1px 4px rgba(19,34,41,0.10)" }}>
            <MoreVertical size={15} color={C.sub} />
          </button>
          {menuOpen && (
            <div onClick={(e) => e.stopPropagation()}
              style={{ position: "absolute", top: 44, right: 12, minWidth: 190,
                background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10,
                boxShadow: "0 8px 24px rgba(19,34,41,0.14)", padding: 4, zIndex: 5 }}>
              <button className="mm-menu-item" onClick={handleToggle} disabled={busy} style={menuItem}>
                {isSuspended ? <UserCheck size={14} /> : <UserMinus size={14} />}
                {isSuspended ? "Unsuspend" : "Suspend"}
              </button>
              <div style={{ height: 1, background: C.line, margin: "4px 0" }} />
              <div style={{ padding: "7px 12px", fontSize: 11.5, color: C.faint, lineHeight: 1.45 }}>
                <Shield size={11} style={{ verticalAlign: "-1px", marginRight: 5 }} />
                Roles, teams and leaders are managed under Manage.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const menuItem = {
  display: "flex", alignItems: "center", gap: 8, width: "100%",
  padding: "9px 12px", border: "none", background: "transparent",
  cursor: "pointer", fontSize: 13, color: C.text,
  textAlign: "left", borderRadius: 6,
};
